import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaAward,
  FaCalendarAlt,
  FaCheck,
  FaClock,
  FaCog,
  FaEdit,
  FaEnvelope,
  FaExternalLinkAlt,
  FaEye,
  FaGlobe,
  FaGraduationCap,
  FaImage,
  FaInfoCircle,
  FaLaptopCode,
  FaMapMarkerAlt,
  FaPalette,
  FaPhoneAlt,
  FaPlus,
  FaRedo,
  FaSave,
  FaSchool,
  FaSearch,
  FaSlidersH,
  FaSpinner,
  FaTimes,
  FaTrash,
  FaTrophy,
  FaUserGraduate,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api/httpClient";
import { API_PATHS } from "../../api/apiPaths";
import { getAllTenants } from "../../api/tenants";
import { getStoredTenantCatalog } from "../../utils/tenantState";
import { useAppTheme } from "../../context/AppThemeContext";
import Seo from "../Seo";
import {
  DEFAULT_PORTAL_CATEGORIES,
  getDefaultPortalContent,
  loadTenantPortalContent,
  saveTenantPortalContent,
} from "../../utils/tenantPortalContent";

const COLOR_PRESETS = [
  { name: "Modern Sapphire", primary: "#2563eb", secondary: "#1d4ed8" },
  { name: "Emerald Academy", primary: "#059669", secondary: "#047857" },
  { name: "Royal Heritage", primary: "#7c3aed", secondary: "#6d28d9" },
  { name: "Crimson Prestige", primary: "#dc2626", secondary: "#b91c1c" },
  { name: "Amber Gold", primary: "#d97706", secondary: "#b45309" },
  { name: "Midnight Slate", primary: "#1e293b", secondary: "#0f172a" },
];

export default function SuperAdminPublicPortalDesigner() {
  let isDarkMode = false;
  try {
    const theme = useAppTheme();
    isDarkMode = Boolean(theme?.isDarkMode);
  } catch {
    isDarkMode = false;
  }
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("identity"); // 'identity' | 'theme' | 'contact' | 'stats' | 'achievements' | 'events'
  const [viewMode, setViewMode] = useState("split"); // 'editor' | 'split' | 'preview'
  const [saving, setSaving] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState(null);

  // Modal states for adding items
  const [showAddAchievement, setShowAddAchievement] = useState(false);
  const [achForm, setAchForm] = useState({
    title: "",
    category: "STEM & Innovation",
    year: String(new Date().getFullYear()),
    statNumber: "",
    statLabel: "",
    description: "",
  });

  const [showAddEvent, setShowAddEvent] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: "",
    category: "Academic",
    date: new Date().toISOString().slice(0, 10),
    time: "09:00 AM – 02:00 PM",
    venue: "Main Campus Auditorium",
    badge: "Upcoming",
    summary: "",
  });

  // Fetch all tenants on mount
  useEffect(() => {
    let mounted = true;
    setLoadingTenants(true);
    getAllTenants()
      .then((remoteList) => {
        if (!mounted) return;
        const localList = getStoredTenantCatalog();
        const mergedMap = new Map();
        [...localList, ...remoteList].forEach((t) => {
          const key = t.id || t.tenantId;
          if (key && !mergedMap.has(key)) {
            mergedMap.set(key, t);
          }
        });
        const combined = Array.from(mergedMap.values()).filter(
          (t) => t.id !== "platform" && t.subdomain !== "platform"
        );
        setTenants(combined);
        if (combined.length > 0) {
          setSelectedTenantId(combined[0].id || combined[0].tenantId);
        }
      })
      .catch(() => {
        if (!mounted) return;
        const localList = getStoredTenantCatalog().filter(
          (t) => t.id !== "platform" && t.subdomain !== "platform"
        );
        setTenants(localList);
        if (localList.length > 0) {
          setSelectedTenantId(localList[0].id || localList[0].tenantId);
        }
      })
      .finally(() => {
        if (mounted) setLoadingTenants(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const selectedTenant = useMemo(() => {
    return tenants.find((t) => (t.id || t.tenantId) === selectedTenantId) || null;
  }, [tenants, selectedTenantId]);

  const tenantKey = useMemo(() => {
    return selectedTenant?.subdomain || selectedTenant?.id || selectedTenant?.tenantId || "default";
  }, [selectedTenant]);

  const schoolName = useMemo(() => {
    return selectedTenant?.schoolName || selectedTenant?.name || "School Campus";
  }, [selectedTenant]);

  // Portal customizable content state
  const [portalContent, setPortalContent] = useState(() =>
    getDefaultPortalContent(schoolName, tenantKey)
  );

  // Load content whenever selected tenant changes
  useEffect(() => {
    if (!tenantKey) return;
    const loaded = loadTenantPortalContent(tenantKey, schoolName);
    setPortalContent({
      ...loaded,
      primaryColor: loaded.primaryColor || selectedTenant?.primaryColor || "#2563eb",
      secondaryColor: loaded.secondaryColor || selectedTenant?.secondaryColor || "#1d4ed8",
      logoUrl: loaded.logoUrl || selectedTenant?.logoUrl || selectedTenant?.schoolLogoUrl || "",
    });
  }, [tenantKey, schoolName, selectedTenant]);

  const handleFieldChange = (field, value) => {
    setPortalContent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStatChange = (id, field, value) => {
    setPortalContent((prev) => ({
      ...prev,
      stats: (prev.stats || []).map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    }));
  };

  const handleAddAchievement = (e) => {
    e.preventDefault();
    if (!achForm.title.trim()) {
      toast.error("Please enter an achievement title.");
      return;
    }
    const newAch = {
      id: `ach-${Date.now()}`,
      ...achForm,
    };
    setPortalContent((prev) => ({
      ...prev,
      achievements: [newAch, ...(prev.achievements || [])],
    }));
    setAchForm({
      title: "",
      category: "STEM & Innovation",
      year: String(new Date().getFullYear()),
      statNumber: "",
      statLabel: "",
      description: "",
    });
    setShowAddAchievement(false);
    toast.success("Achievement added to draft.");
  };

  const handleDeleteAchievement = (id) => {
    setPortalContent((prev) => ({
      ...prev,
      achievements: (prev.achievements || []).filter((a) => a.id !== id),
    }));
    toast.info("Achievement removed.");
  };

  const handleAddEvent = (e) => {
    e.preventDefault();
    if (!eventForm.title.trim()) {
      toast.error("Please enter an event title.");
      return;
    }
    const newEvt = {
      id: `evt-${Date.now()}`,
      ...eventForm,
    };
    setPortalContent((prev) => ({
      ...prev,
      events: [...(prev.events || []), newEvt],
    }));
    setEventForm({
      title: "",
      category: "Academic",
      date: new Date().toISOString().slice(0, 10),
      time: "09:00 AM – 02:00 PM",
      venue: "Main Campus Auditorium",
      badge: "Upcoming",
      summary: "",
    });
    setShowAddEvent(false);
    toast.success("Event added to draft.");
  };

  const handleDeleteEvent = (id) => {
    setPortalContent((prev) => ({
      ...prev,
      events: (prev.events || []).filter((e) => e.id !== id),
    }));
    toast.info("Event removed.");
  };

  const handleResetToDefaults = () => {
    if (!window.confirm("Reset this school's public portal design to default ClassNX template?")) return;
    const defaults = getDefaultPortalContent(schoolName, tenantKey);
    setPortalContent(defaults);
    saveTenantPortalContent(tenantKey, defaults);
    toast.info("Portal reset to standard template defaults.");
  };

  const handleSaveAndPublish = async () => {
    if (!tenantKey) return;
    setSaving(true);
    try {
      // 1. Persist to local multi-tenant storage for instant preview & resolution
      const localSuccess = saveTenantPortalContent(tenantKey, portalContent);
      if (!localSuccess) {
        throw new Error("Unable to save to client storage.");
      }

      // 2. Dispatch event for live components
      window.dispatchEvent(
        new CustomEvent("school-portal:updated", {
          detail: { tenantId: tenantKey, content: portalContent },
        })
      );

      // 3. Sync to backend API endpoint
      const tenantId = selectedTenant?.id || selectedTenant?.tenantId || tenantKey;
      try {
        await api.put(`/api/super-admin/tenants/${encodeURIComponent(tenantId)}/portal-content`, portalContent, {
          withCredentials: true,
        });
      } catch (backendError) {
        // Backend endpoint integration notice: If the endpoint is pending implementation,
        // local persistence is active.
        console.warn("Backend portal sync pending endpoint:", backendError?.message);
      }

      setLastSavedTime(new Date().toLocaleTimeString());
      toast.success(`Published public portal design for ${schoolName}!`);
    } catch (error) {
      toast.error(error?.message || "Failed to publish portal design.");
    } finally {
      setSaving(false);
    }
  };

  const filteredTenants = useMemo(() => {
    if (!searchTerm.trim()) return tenants;
    const term = searchTerm.toLowerCase();
    return tenants.filter(
      (t) =>
        (t.name && t.name.toLowerCase().includes(term)) ||
        (t.schoolName && t.schoolName.toLowerCase().includes(term)) ||
        (t.subdomain && t.subdomain.toLowerCase().includes(term))
    );
  }, [tenants, searchTerm]);

  const surface = isDarkMode ? "bg-slate-900 border-slate-800 text-slate-100" : "bg-white border-slate-200 text-slate-900";
  const inputBg = isDarkMode ? "bg-slate-800 border-slate-700 text-white placeholder-slate-500" : "bg-slate-50 border-slate-300 text-slate-900 placeholder-slate-400";
  const cardBg = isDarkMode ? "bg-slate-800/70 border-slate-700" : "bg-slate-50/80 border-slate-200";

  return (
    <div className={`min-h-full py-6 px-4 sm:px-6 lg:px-8 ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-100 text-slate-900"}`}>
      <Seo
        title="Super Admin School Public Portal Designer | ClassNX"
        description="Design and customize public home pages for any tenant school on the ClassNX platform."
      />

      {/* Header Banner */}
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-4 rounded-3xl bg-gradient-to-r from-blue-700 via-indigo-700 to-cyan-600 p-6 sm:p-8 text-white shadow-xl shadow-blue-900/20 md:flex-row md:items-center md:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-cyan-200">
              <FaLaptopCode /> Super Admin Customization Suite
            </span>
            <h1 className="mt-3 text-2xl font-black sm:text-3xl tracking-tight">
              School Public Portal Designer
            </h1>
            <p className="mt-2 text-sm text-blue-100 leading-relaxed">
              Design any tenant’s custom public landing page. Customize hero highlights, school story, statistics counters, achievements, and events with real-time preview.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleResetToDefaults}
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-4 py-2.5 text-xs font-bold text-white transition hover:bg-white/20"
            >
              <FaRedo /> Reset Defaults
            </button>
            <button
              type="button"
              onClick={handleSaveAndPublish}
              disabled={saving || !selectedTenant}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400 disabled:opacity-50"
            >
              {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
              {saving ? "Publishing..." : "Publish Portal"}
            </button>
          </div>
        </div>

        {/* Tenant Selector & View Mode Switcher */}
        <div className={`mt-6 flex flex-col gap-4 rounded-2xl border p-4 shadow-sm md:flex-row md:items-center md:justify-between ${surface}`}>
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
            <label htmlFor="tenant-select" className="text-xs font-bold uppercase tracking-wider text-slate-500 shrink-0">
              Select School Tenant:
            </label>
            <div className="relative flex-1 max-w-md">
              <select
                id="tenant-select"
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                className={`w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none transition ${inputBg}`}
                disabled={loadingTenants}
              >
                {filteredTenants.map((t) => (
                  <option key={t.id || t.tenantId} value={t.id || t.tenantId}>
                    {t.name || t.schoolName} ({t.subdomain ? `${t.subdomain}.classnx.com` : "no subdomain"})
                  </option>
                ))}
              </select>
            </div>
            {selectedTenant?.subdomain && (
              <a
                href={`https://${selectedTenant.subdomain}.classnx.com/`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs font-bold text-blue-600 dark:text-blue-400 hover:bg-blue-500/20"
              >
                <FaExternalLinkAlt className="text-[10px]" /> View Live Subdomain
              </a>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-400 mr-1">View Mode:</span>
            <div className="inline-flex rounded-xl border p-1 bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setViewMode("editor")}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${viewMode === "editor" ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}
              >
                Editor Only
              </button>
              <button
                type="button"
                onClick={() => setViewMode("split")}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${viewMode === "split" ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}
              >
                Split View
              </button>
              <button
                type="button"
                onClick={() => setViewMode("preview")}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${viewMode === "preview" ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}
              >
                Full Preview
              </button>
            </div>
            {lastSavedTime && (
              <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 ml-2">
                Saved at {lastSavedTime}
              </span>
            )}
          </div>
        </div>

        {/* Main Grid: Editor & Preview */}
        <div className="mt-6 grid gap-6 lg:grid-cols-12">
          {/* Editor Column */}
          {(viewMode === "editor" || viewMode === "split") && (
            <div className={`${viewMode === "split" ? "lg:col-span-6 xl:col-span-5" : "lg:col-span-12"} space-y-6`}>
              <div className={`overflow-hidden rounded-2xl border shadow-sm ${surface}`}>
                {/* Editor Tabs */}
                <div className="flex flex-wrap border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 p-2 gap-1">
                  {[
                    { id: "identity", label: "Identity & Story", icon: FaSchool },
                    { id: "theme", label: "Theme & Colors", icon: FaPalette },
                    { id: "contact", label: "Contact & Hours", icon: FaMapMarkerAlt },
                    { id: "stats", label: "Key Metrics", icon: FaSlidersH },
                    { id: "achievements", label: `Achievements (${portalContent.achievements?.length || 0})`, icon: FaTrophy },
                    { id: "events", label: `Events (${portalContent.events?.length || 0})`, icon: FaCalendarAlt },
                  ].map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"}`}
                      >
                        <Icon className="text-xs" /> {tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* Tab Content Form */}
                <div className="p-5 sm:p-6 space-y-5">
                  {/* TAB 1: IDENTITY & STORY */}
                  {activeTab === "identity" && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">School Display Name</label>
                        <input
                          type="text"
                          value={schoolName}
                          readOnly
                          className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold opacity-80 cursor-not-allowed ${inputBg}`}
                        />
                        <p className="mt-1 text-[11px] text-slate-400">School name is governed by the tenant registry.</p>
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Public Tagline / Slogan</label>
                        <input
                          type="text"
                          value={portalContent.tagline || ""}
                          onChange={(e) => handleFieldChange("tagline", e.target.value)}
                          placeholder="e.g. Inspiring Excellence, Fostering Innovation"
                          className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none transition ${inputBg}`}
                        />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Established Year</label>
                          <input
                            type="text"
                            value={portalContent.establishedYear || ""}
                            onChange={(e) => handleFieldChange("establishedYear", e.target.value)}
                            placeholder="e.g. 2015"
                            className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none transition ${inputBg}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Affiliation / Curriculum</label>
                          <input
                            type="text"
                            value={portalContent.affiliation || ""}
                            onChange={(e) => handleFieldChange("affiliation", e.target.value)}
                            placeholder="e.g. CBSE & International Curriculum"
                            className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none transition ${inputBg}`}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Hero Welcome Title</label>
                        <input
                          type="text"
                          value={portalContent.welcomeTitle || ""}
                          onChange={(e) => handleFieldChange("welcomeTitle", e.target.value)}
                          placeholder={`Welcome to ${schoolName}`}
                          className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none transition ${inputBg}`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">School Narrative / Story</label>
                        <textarea
                          rows={4}
                          value={portalContent.welcomeMessage || ""}
                          onChange={(e) => handleFieldChange("welcomeMessage", e.target.value)}
                          placeholder="Describe the school's mission, values, and community..."
                          className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm leading-relaxed outline-none transition ${inputBg}`}
                        />
                      </div>
                    </div>
                  )}

                  {/* TAB 2: THEME & BRANDING */}
                  {activeTab === "theme" && (
                    <div className="space-y-5">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Color Presets</label>
                        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                          {COLOR_PRESETS.map((preset) => (
                            <button
                              key={preset.name}
                              type="button"
                              onClick={() => {
                                handleFieldChange("primaryColor", preset.primary);
                                handleFieldChange("secondaryColor", preset.secondary);
                              }}
                              className={`flex items-center gap-2.5 rounded-xl border p-2.5 text-left text-xs font-semibold transition hover:border-blue-500 ${cardBg}`}
                            >
                              <span
                                className="h-5 w-5 rounded-full shadow-sm"
                                style={{
                                  background: `linear-gradient(135deg, ${preset.primary} 0%, ${preset.secondary} 100%)`,
                                }}
                              />
                              <span className="truncate">{preset.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Primary Brand Color</label>
                          <div className="mt-1.5 flex items-center gap-2">
                            <input
                              type="color"
                              value={portalContent.primaryColor || "#2563eb"}
                              onChange={(e) => handleFieldChange("primaryColor", e.target.value)}
                              className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300 dark:border-slate-700 bg-transparent p-1"
                            />
                            <input
                              type="text"
                              value={portalContent.primaryColor || "#2563eb"}
                              onChange={(e) => handleFieldChange("primaryColor", e.target.value)}
                              className={`flex-1 rounded-xl border px-3 py-2 text-sm font-mono outline-none ${inputBg}`}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Secondary Accent Color</label>
                          <div className="mt-1.5 flex items-center gap-2">
                            <input
                              type="color"
                              value={portalContent.secondaryColor || "#1d4ed8"}
                              onChange={(e) => handleFieldChange("secondaryColor", e.target.value)}
                              className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300 dark:border-slate-700 bg-transparent p-1"
                            />
                            <input
                              type="text"
                              value={portalContent.secondaryColor || "#1d4ed8"}
                              onChange={(e) => handleFieldChange("secondaryColor", e.target.value)}
                              className={`flex-1 rounded-xl border px-3 py-2 text-sm font-mono outline-none ${inputBg}`}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">School Logo URL</label>
                        <input
                          type="url"
                          value={portalContent.logoUrl || ""}
                          onChange={(e) => handleFieldChange("logoUrl", e.target.value)}
                          placeholder="https://example.com/school-logo.png"
                          className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none transition ${inputBg}`}
                        />
                        {portalContent.logoUrl && (
                          <div className="mt-3 flex items-center gap-3 rounded-xl border p-3 bg-white dark:bg-slate-800">
                            <img
                              src={portalContent.logoUrl}
                              alt="Logo preview"
                              className="h-10 w-10 object-contain rounded-lg"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                            <span className="text-xs text-slate-500">Logo preview verified</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* TAB 3: CONTACT & HOURS */}
                  {activeTab === "contact" && (
                    <div className="space-y-4">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Contact Email</label>
                          <input
                            type="email"
                            value={portalContent.contactEmail || ""}
                            onChange={(e) => handleFieldChange("contactEmail", e.target.value)}
                            placeholder="admissions@school.edu"
                            className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none transition ${inputBg}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Contact Phone</label>
                          <input
                            type="tel"
                            value={portalContent.contactPhone || ""}
                            onChange={(e) => handleFieldChange("contactPhone", e.target.value)}
                            placeholder="+91 (080) 4567-8900"
                            className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none transition ${inputBg}`}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Campus Physical Address</label>
                        <input
                          type="text"
                          value={portalContent.campusAddress || ""}
                          onChange={(e) => handleFieldChange("campusAddress", e.target.value)}
                          placeholder="Main Campus Boulevard, Knowledge Park District"
                          className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none transition ${inputBg}`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Office & Visiting Hours</label>
                        <input
                          type="text"
                          value={portalContent.officeHours || ""}
                          onChange={(e) => handleFieldChange("officeHours", e.target.value)}
                          placeholder="Monday – Saturday: 8:00 AM – 4:30 PM"
                          className={`mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm font-semibold outline-none transition ${inputBg}`}
                        />
                      </div>
                    </div>
                  )}

                  {/* TAB 4: KEY METRICS */}
                  {activeTab === "stats" && (
                    <div className="space-y-4">
                      <p className="text-xs text-slate-500">Configure key numeric statistics highlighted on the public home page strip.</p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {(portalContent.stats || []).map((stat, idx) => (
                          <div key={stat.id || idx} className={`rounded-xl border p-3.5 space-y-2 ${cardBg}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Metric #{idx + 1}</span>
                            </div>
                            <input
                              type="text"
                              value={stat.number || ""}
                              onChange={(e) => handleStatChange(stat.id, "number", e.target.value)}
                              placeholder="e.g. 100% or 14:1"
                              className={`w-full rounded-lg border px-2.5 py-1.5 text-sm font-bold outline-none ${inputBg}`}
                            />
                            <input
                              type="text"
                              value={stat.label || ""}
                              onChange={(e) => handleStatChange(stat.id, "label", e.target.value)}
                              placeholder="e.g. Board Exam Pass Rate"
                              className={`w-full rounded-lg border px-2.5 py-1.5 text-xs font-medium outline-none ${inputBg}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* TAB 5: ACHIEVEMENTS */}
                  {activeTab === "achievements" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500">Highlight student and school honors on the public portal.</p>
                        <button
                          type="button"
                          onClick={() => setShowAddAchievement(true)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-blue-700"
                        >
                          <FaPlus /> Add Achievement
                        </button>
                      </div>

                      {showAddAchievement && (
                        <form onSubmit={handleAddAchievement} className={`rounded-xl border p-4 space-y-3 ${cardBg}`}>
                          <div className="flex items-center justify-between">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-blue-600">New Achievement</h3>
                            <button type="button" onClick={() => setShowAddAchievement(false)} className="text-slate-400 hover:text-slate-600">
                              <FaTimes />
                            </button>
                          </div>
                          <input
                            type="text"
                            required
                            placeholder="Title (e.g. National Robotics Olympiad)"
                            value={achForm.title}
                            onChange={(e) => setAchForm({ ...achForm, title: e.target.value })}
                            className={`w-full rounded-lg border px-3 py-2 text-sm font-medium ${inputBg}`}
                          />
                          <div className="grid gap-2 sm:grid-cols-3">
                            <select
                              value={achForm.category}
                              onChange={(e) => setAchForm({ ...achForm, category: e.target.value })}
                              className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium ${inputBg}`}
                            >
                              {DEFAULT_PORTAL_CATEGORIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              placeholder="Year (e.g. 2025)"
                              value={achForm.year}
                              onChange={(e) => setAchForm({ ...achForm, year: e.target.value })}
                              className={`rounded-lg border px-2.5 py-1.5 text-xs ${inputBg}`}
                            />
                            <input
                              type="text"
                              placeholder="Badge (e.g. 1st Place)"
                              value={achForm.statNumber}
                              onChange={(e) => setAchForm({ ...achForm, statNumber: e.target.value })}
                              className={`rounded-lg border px-2.5 py-1.5 text-xs ${inputBg}`}
                            />
                          </div>
                          <textarea
                            rows={2}
                            placeholder="Brief description of the accomplishment..."
                            value={achForm.description}
                            onChange={(e) => setAchForm({ ...achForm, description: e.target.value })}
                            className={`w-full rounded-lg border px-3 py-2 text-xs leading-relaxed ${inputBg}`}
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setShowAddAchievement(false)}
                              className="rounded-lg border px-3 py-1.5 text-xs font-bold text-slate-500"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-emerald-700"
                            >
                              Add to Portal
                            </button>
                          </div>
                        </form>
                      )}

                      <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
                        {(portalContent.achievements || []).map((ach) => (
                          <div key={ach.id} className={`flex items-start justify-between gap-3 rounded-xl border p-3 ${cardBg}`}>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-400">
                                  {ach.category}
                                </span>
                                <span className="text-[11px] font-semibold text-slate-400">{ach.year}</span>
                                {ach.statNumber && (
                                  <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-600 dark:text-amber-400">
                                    {ach.statNumber}
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-sm font-bold">{ach.title}</p>
                              {ach.description && <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{ach.description}</p>}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteAchievement(ach.id)}
                              className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                              title="Delete achievement"
                            >
                              <FaTrash className="text-xs" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* TAB 6: EVENTS & NOTICES */}
                  {activeTab === "events" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500">Broadcast upcoming academic schedules and public notices.</p>
                        <button
                          type="button"
                          onClick={() => setShowAddEvent(true)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-blue-700"
                        >
                          <FaPlus /> Add Event
                        </button>
                      </div>

                      {showAddEvent && (
                        <form onSubmit={handleAddEvent} className={`rounded-xl border p-4 space-y-3 ${cardBg}`}>
                          <div className="flex items-center justify-between">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-blue-600">New Public Event</h3>
                            <button type="button" onClick={() => setShowAddEvent(false)} className="text-slate-400 hover:text-slate-600">
                              <FaTimes />
                            </button>
                          </div>
                          <input
                            type="text"
                            required
                            placeholder="Event Title (e.g. Science Expo)"
                            value={eventForm.title}
                            onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                            className={`w-full rounded-lg border px-3 py-2 text-sm font-medium ${inputBg}`}
                          />
                          <div className="grid gap-2 sm:grid-cols-3">
                            <input
                              type="date"
                              value={eventForm.date}
                              onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                              className={`rounded-lg border px-2.5 py-1.5 text-xs ${inputBg}`}
                            />
                            <input
                              type="text"
                              placeholder="Time (e.g. 10:00 AM)"
                              value={eventForm.time}
                              onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                              className={`rounded-lg border px-2.5 py-1.5 text-xs ${inputBg}`}
                            />
                            <input
                              type="text"
                              placeholder="Badge (e.g. Flagship)"
                              value={eventForm.badge}
                              onChange={(e) => setEventForm({ ...eventForm, badge: e.target.value })}
                              className={`rounded-lg border px-2.5 py-1.5 text-xs ${inputBg}`}
                            />
                          </div>
                          <input
                            type="text"
                            placeholder="Venue (e.g. Campus Sports Arena)"
                            value={eventForm.venue}
                            onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })}
                            className={`w-full rounded-lg border px-3 py-2 text-xs ${inputBg}`}
                          />
                          <textarea
                            rows={2}
                            placeholder="Brief summary for parents and visitors..."
                            value={eventForm.summary}
                            onChange={(e) => setEventForm({ ...eventForm, summary: e.target.value })}
                            className={`w-full rounded-lg border px-3 py-2 text-xs leading-relaxed ${inputBg}`}
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setShowAddEvent(false)}
                              className="rounded-lg border px-3 py-1.5 text-xs font-bold text-slate-500"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-emerald-700"
                            >
                              Add Event
                            </button>
                          </div>
                        </form>
                      )}

                      <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
                        {(portalContent.events || []).map((evt) => (
                          <div key={evt.id} className={`flex items-start justify-between gap-3 rounded-xl border p-3 ${cardBg}`}>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                                  {evt.category}
                                </span>
                                <span className="text-[11px] font-semibold text-slate-400">{evt.date}</span>
                                {evt.badge && (
                                  <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-400">
                                    {evt.badge}
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-sm font-bold">{evt.title}</p>
                              <p className="mt-0.5 text-xs text-slate-500">
                                {evt.time} · {evt.venue}
                              </p>
                              {evt.summary && <p className="mt-1 text-xs text-slate-400 line-clamp-2">{evt.summary}</p>}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteEvent(evt.id)}
                              className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                              title="Delete event"
                            >
                              <FaTrash className="text-xs" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Live Preview Column */}
          {(viewMode === "preview" || viewMode === "split") && (
            <div className={`${viewMode === "split" ? "lg:col-span-6 xl:col-span-7" : "lg:col-span-12"}`}>
              <div className={`overflow-hidden rounded-2xl border shadow-lg ${surface}`}>
                {/* Browser-like Preview Header */}
                <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-rose-400 inline-block" />
                    <span className="h-3 w-3 rounded-full bg-amber-400 inline-block" />
                    <span className="h-3 w-3 rounded-full bg-emerald-400 inline-block" />
                    <span className="ml-2 font-mono text-xs text-slate-500 truncate max-w-xs sm:max-w-md">
                      https://{selectedTenant?.subdomain || "school"}.classnx.com
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                    <FaEye /> Live Canvas Preview
                  </span>
                </div>

                {/* Simulated Public Portal Container */}
                <div className="max-h-[820px] overflow-y-auto bg-slate-50 dark:bg-slate-950 p-4 sm:p-6 space-y-6">
                  {/* Public Hero Mockup */}
                  <div
                    className="relative overflow-hidden rounded-3xl p-6 sm:p-10 text-white shadow-xl"
                    style={{
                      background: `linear-gradient(135deg, ${portalContent.primaryColor || "#2563eb"} 0%, ${portalContent.secondaryColor || "#1d4ed8"} 100%)`,
                    }}
                  >
                    <div className="relative z-10 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        {portalContent.logoUrl ? (
                          <img
                            src={portalContent.logoUrl}
                            alt=""
                            className="h-12 w-12 rounded-2xl bg-white p-1.5 object-contain shadow-md"
                          />
                        ) : (
                          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/20 font-black text-white text-lg">
                            {schoolName.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="text-base font-black tracking-tight">{schoolName}</p>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-200">
                            {portalContent.affiliation || "Official Academic Campus"}
                          </p>
                        </div>
                      </div>
                      <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-semibold backdrop-blur-md">
                        Est. {portalContent.establishedYear || "2015"}
                      </span>
                    </div>

                    <div className="relative z-10 mt-8 max-w-lg">
                      <p className="text-xs font-bold uppercase tracking-widest text-cyan-200">
                        {portalContent.tagline || "Inspiring Excellence, Fostering Innovation"}
                      </p>
                      <h2 className="mt-2 text-2xl sm:text-3xl font-black leading-tight">
                        {portalContent.welcomeTitle || `Welcome to ${schoolName}`}
                      </h2>
                      <p className="mt-3 text-xs sm:text-sm leading-relaxed text-blue-50/90 line-clamp-3">
                        {portalContent.welcomeMessage}
                      </p>
                    </div>
                  </div>

                  {/* Public Metrics Strip */}
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {(portalContent.stats || []).map((s, idx) => (
                      <div
                        key={s.id || idx}
                        className={`rounded-2xl border p-4 text-center shadow-sm ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"}`}
                      >
                        <p
                          className="text-2xl font-black"
                          style={{ color: portalContent.primaryColor || "#2563eb" }}
                        >
                          {s.number}
                        </p>
                        <p className="mt-1 text-xs font-bold uppercase tracking-wider text-slate-500">
                          {s.label}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Achievements Preview */}
                  <div className={`rounded-2xl border p-5 shadow-sm ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-blue-600">Honors & Awards</span>
                        <h3 className="text-base font-black">Campus Achievements</h3>
                      </div>
                      <span className="rounded-full bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 text-[11px] font-bold text-blue-600 dark:text-blue-400">
                        {portalContent.achievements?.length || 0} Total
                      </span>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      {(portalContent.achievements || []).slice(0, 4).map((a) => (
                        <div key={a.id} className={`rounded-xl border p-3.5 space-y-1.5 ${isDarkMode ? "bg-slate-800/50 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
                          <div className="flex items-center justify-between">
                            <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[9px] font-bold text-blue-600 dark:text-blue-400">
                              {a.category}
                            </span>
                            <span className="text-[10px] font-bold text-amber-500">{a.statNumber}</span>
                          </div>
                          <p className="text-xs font-bold text-slate-900 dark:text-white line-clamp-1">{a.title}</p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">{a.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Events & Contact Strip */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    {/* Events list */}
                    <div className={`rounded-2xl border p-4 shadow-sm ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"}`}>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Calendar</span>
                      <h3 className="text-sm font-black mb-3">Upcoming Events</h3>
                      <div className="space-y-2">
                        {(portalContent.events || []).slice(0, 3).map((e) => (
                          <div key={e.id} className="flex items-start gap-2.5 border-b border-slate-100 dark:border-slate-800 pb-2">
                            <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-900/30 text-[10px] font-bold text-blue-600 dark:text-blue-400">
                              <span>{e.date ? e.date.slice(5) : "—"}</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-bold">{e.title}</p>
                              <p className="text-[10px] text-slate-400">{e.venue}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Contact details */}
                    <div className={`rounded-2xl border p-4 shadow-sm ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"}`}>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">Campus Contact</span>
                      <h3 className="text-sm font-black mb-3">Visitor Desk</h3>
                      <div className="space-y-2 text-xs text-slate-600 dark:text-slate-400">
                        <p className="flex items-center gap-2">
                          <FaEnvelope className="text-blue-500 shrink-0" />
                          <span className="truncate">{portalContent.contactEmail || "contact@school.edu"}</span>
                        </p>
                        <p className="flex items-center gap-2">
                          <FaPhoneAlt className="text-emerald-500 shrink-0" />
                          <span>{portalContent.contactPhone || "+91 (080) 4567-8900"}</span>
                        </p>
                        <p className="flex items-center gap-2">
                          <FaMapMarkerAlt className="text-rose-500 shrink-0" />
                          <span className="truncate">{portalContent.campusAddress || "Main Campus District"}</span>
                        </p>
                        <p className="flex items-center gap-2">
                          <FaClock className="text-amber-500 shrink-0" />
                          <span className="truncate">{portalContent.officeHours || "Mon – Sat: 8:00 AM – 4:30 PM"}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
